/**
 * @file TaskAssigneeControl.tsx
 * @description Исполнитель в окне задачи (phase19.31, crm.md T2–T3): имя и
 * кнопка «Назначить» / «Сменить». По нажатию — список сотрудников магазина
 * с правом CRM (`task-assignees`) и пункт «Без исполнителя»; выбор сразу
 * сохраняется. Кнопки нет, если менять нельзя (выполненная задача, нет
 * права) — тогда только имя.
 */

import { useState } from "react";

import { Button } from "../../ui/button";
import { SelectField } from "../../ui/SelectField";
import { useTaskAssignees } from "./useTaskAssignees";

/** Значение пункта «Без исполнителя» в списке выбора. */
const NONE = "__none";

interface Props {
  assigneeUserId: string | null;
  assigneeName: string | null;
  canAssign: boolean;
  saving: boolean;
  /** Идёт другое действие с задачей — менять исполнителя нельзя. */
  locked?: boolean;
  onAssign: (userId: string | null) => void;
}

export function TaskAssigneeControl({ assigneeUserId, assigneeName, canAssign, saving, locked = false, onAssign }: Props) {
  const [picking, setPicking] = useState(false);

  if (picking && canAssign) {
    return (
      <AssigneePicker
        value={assigneeUserId ?? NONE}
        saving={saving || locked}
        onCancel={() => setPicking(false)}
        onPick={(value) => {
          setPicking(false);
          const next = value === NONE ? null : value;
          if (next !== assigneeUserId) onAssign(next);
        }}
      />
    );
  }

  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className={assigneeName ? "text-on-surface" : "text-on-surface-variant"}>{assigneeName ?? "Без исполнителя"}</span>
      {canAssign ? (
        <Button variant="outline" loading={saving} disabled={locked} onClick={() => setPicking(true)}>
          {assigneeUserId ? "Сменить" : "Назначить"}
        </Button>
      ) : null}
    </span>
  );
}

/** Выбор сотрудника: список загружается при открытии. */
function AssigneePicker({ value, saving, onCancel, onPick }: {
  value: string;
  saving: boolean;
  onCancel: () => void;
  onPick: (value: string) => void;
}) {
  const { options, loading, failed } = useTaskAssignees();
  return (
    <span className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
      <SelectField
        ariaLabel="Исполнитель"
        value={value}
        onChange={onPick}
        options={[...options, { value: NONE, label: "Без исполнителя" }]}
        placeholder={loading ? "Загрузка…" : "Выберите сотрудника"}
        disabled={loading || failed || saving}
        className="min-w-0 sm:flex-1"
      />
      <Button variant="ghost" onClick={onCancel}>
        Отмена
      </Button>
      {failed ? <span className="type-caption text-[var(--destructive-text)]">Не удалось загрузить список сотрудников</span> : null}
    </span>
  );
}
